import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  type BillingMachineResource,
  BillingMachinesController_attach_v1,
  type OutletResource,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import { Form } from '@repo/ui/components/form';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { messageForFailure } from '@/api/failure';
import { systemClock } from '@/lib/clock';
import { IDEMPOTENCY_KEY_HEADER } from '@/lib/idempotency-key';
import { attemptFor, type WriteAttempt } from '@/lib/write-attempt';

import { FormDialogContent } from '../auth/AuthFrame';
import { currentBusinessDate } from '../reporting/reporting-period';
import { DayField, NO_OUTLET, OutletField } from './machine-fields';

/**
 * Moving a machine: `POST /v1/billing-machines/{id}/attachments`, owner-only.
 *
 * **A move is dated.** Bills dated before `effectiveFrom` stay booked to the
 * outlet the machine was at then; only bills from that day on follow it. The
 * API refuses a date on or before a bill it has already imported
 * (`MACHINE_ATTACHMENT_BEHIND_IMPORTED_BILLS`), which lands on the date field.
 *
 * "Attached to nothing" sends `outletId: null` and detaches the machine; bills
 * dated from then on are refused until it is attached again.
 */

const OUTLET_REQUIRED = 'Choose the outlet it sells for, or Attached to nothing.';
const DAY_REQUIRED = 'Choose the day the move takes effect.';

const attachSchema = z.object({
  outletId: z.string().min(1, OUTLET_REQUIRED),
  effectiveFrom: z.string().min(1, DAY_REQUIRED),
});

type AttachValues = z.infer<typeof attachSchema>;

export function AttachMachineDialog({
  machine,
  outlets,
  onClose,
  onAttached,
}: {
  /** The machine being moved, or `null` when the dialog is shut. */
  machine: BillingMachineResource | null;
  outlets: readonly OutletResource[];
  onClose: () => void;
  onAttached: (machine: BillingMachineResource) => void;
}) {
  return (
    <Dialog
      open={machine !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <FormDialogContent>
        <DialogHeader>
          <DialogTitle>Move machine</DialogTitle>
          <DialogDescription>
            Bills dated from the day you choose are booked to the new outlet. Bills before it stay
            where they were.
          </DialogDescription>
        </DialogHeader>

        {machine !== null && (
          <AttachMachineForm machine={machine} outlets={outlets} onAttached={onAttached} />
        )}
      </FormDialogContent>
    </Dialog>
  );
}

function AttachMachineForm({
  machine,
  outlets,
  onAttached,
}: {
  machine: BillingMachineResource;
  outlets: readonly OutletResource[];
  onAttached: (machine: BillingMachineResource) => void;
}) {
  const client = useApiClient();
  const today = currentBusinessDate(systemClock);

  const form = useForm({
    resolver: zodResolver(attachSchema),
    mode: 'onTouched',
    defaultValues: { outletId: '', effectiveFrom: today },
  });

  const [formError, setFormError] = useState<string | null>(null);

  // The key belongs to the body: held while it stands, so a retry after a lost
  // answer replays; fresh once it changes, so a corrected field after a failed
  // attempt is not refused as a reused key. See `lib/write-attempt.ts`.
  const attempt = useRef<WriteAttempt | null>(null);

  const pending = form.formState.isSubmitting;

  const handleSubmit = async ({ outletId, effectiveFrom }: AttachValues) => {
    if (pending) return;

    setFormError(null);

    try {
      const body = { outletId: outletId === NO_OUTLET ? null : outletId, effectiveFrom };
      const sending = attemptFor(attempt.current, JSON.stringify(body));
      attempt.current = sending;

      onAttached(
        await client.request(BillingMachinesController_attach_v1, {
          path: { id: machine.id },
          body,
          headers: { [IDEMPOTENCY_KEY_HEADER]: sending.key },
        }),
      );
    } catch (error) {
      const code = error instanceof ApiError ? error.code : null;

      if (code === 'MACHINE_ATTACHMENT_BEHIND_IMPORTED_BILLS') {
        form.setError('effectiveFrom', { message: messageForFailure(error) });
      } else {
        setFormError(messageForFailure(error));
      }
    }
  };

  return (
    <Form {...form}>
      <form
        className="space-y-5"
        noValidate
        onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
      >
        <p className="rounded-2xl bg-muted px-4 py-3 text-sm text-foreground">
          <span className="font-mono numeric">{machine.machineCode}</span> — {machine.name}
        </p>

        <OutletField
          control={form.control}
          name="outletId"
          outlets={outlets}
          idPrefix="attach"
          allowNothing
        />

        <DayField
          control={form.control}
          name="effectiveFrom"
          label="From"
          maxDay={today}
          idPrefix="attach"
          description="Must be after the last bill already imported from this machine, and not later than today."
        />

        {formError !== null && (
          <Alert variant="destructive">
            <IconAlertTriangle />
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button type="submit" disabled={pending}>
            {pending ? 'Moving…' : 'Move machine'}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
